/**
 * Tool Error Utilities
 *
 * Utility functions for detecting and handling tool errors in AI responses.
 * Used to display appropriate error messages via AIErrorDisplay component
 * and to skip follow-up streaming when all tools fail.
 */

/**
 * Map tool names to error types for AIErrorDisplay
 * This maps various tool name variations to their corresponding error type
 */
const TOOL_ERROR_TYPE_MAP = {
  // Web search tools
  web_search: "web_search",
  firecrawl_search: "web_search",
  places_search: "web_search",
  search: "web_search",

  // Image generation tools
  image_gen: "image_generation",
  image_generation: "image_generation",
  image_edit: "image_generation",
  dalle: "image_generation",
  midjourney: "image_generation",
  stable_diffusion: "image_generation",
  stability: "image_generation",

  // Code execution tools
  code_interpreter: "code_interpreter",
  python: "code_interpreter",
  execute_code: "code_interpreter",
  run_code: "code_interpreter",

  // Canvas tools
  canvas_create: "canvas",
  canvas_edit: "canvas",
  canvas_stream: "canvas",
  canvas: "canvas",
};

/**
 * Get error type for AIErrorDisplay based on tool name
 * @param {string} toolName - The name of the tool
 * @returns {string} Error type for AIErrorDisplay
 */
export function getToolErrorType(toolName) {
  if (!toolName) return "tool_error";

  const normalizedName = toolName.toLowerCase().trim();

  // Direct match
  if (TOOL_ERROR_TYPE_MAP[normalizedName]) {
    return TOOL_ERROR_TYPE_MAP[normalizedName];
  }

  // Partial match for compound tool names
  for (const [key, value] of Object.entries(TOOL_ERROR_TYPE_MAP)) {
    if (normalizedName.includes(key)) {
      return value;
    }
  }

  // Default fallback
  return "tool_error";
}

/**
 * Check if a tool result contains an error
 * Handles various error formats from different tool implementations
 * @param {Object} result - Tool result object
 * @returns {boolean} True if result contains an error
 */
export function isToolError(result) {
  if (!result) return false;

  // Check various error indicators
  return (
    result.error === true ||
    result.isError === true ||
    result.success === false ||
    (typeof result.error === "string" && result.error.length > 0) ||
    (typeof result.message === "string" &&
      result.error !== undefined &&
      result.error !== false)
  );
}

/**
 * Extract error message from a tool result
 * @param {Object} result - Tool result object
 * @returns {string|null} Error message or null if not found
 */
export function getToolErrorMessage(result) {
  if (!result) return null;

  // Check various places where error messages might be stored
  if (typeof result.error === "string") {
    return result.error;
  }
  if (typeof result.message === "string") {
    return result.message;
  }
  if (typeof result.details === "string") {
    return result.details;
  }
  if (result.error && typeof result.error.message === "string") {
    return result.error.message;
  }

  return null;
}

/**
 * Extract tool errors from tool calls array
 * @param {Array} toolCalls - Array of tool call objects
 * @returns {Array} Array of error objects with tool info
 */
export function extractToolErrors(toolCalls) {
  if (!Array.isArray(toolCalls)) return [];

  const errors = [];

  for (const toolCall of toolCalls) {
    const result = toolCall.result;
    const toolName = toolCall.function?.name || toolCall.name;

    if (isToolError(result)) {
      errors.push({
        toolName,
        toolCallId: toolCall.id,
        error: getToolErrorMessage(result) || "Tool execution failed",
        errorType: getToolErrorType(toolName),
        result: result,
      });
    }
  }

  return errors;
}

/**
 * Check if web search data contains an error
 * @param {Object} webSearchData - Web search data object
 * @returns {Object|null} Error info if present, null otherwise
 */
export function getWebSearchError(webSearchData) {
  if (!webSearchData) return null;

  if (isToolError(webSearchData)) {
    return {
      toolName: "web_search",
      error: getToolErrorMessage(webSearchData) || "Web search failed",
      errorType: "web_search",
    };
  }

  return null;
}

/**
 * Check if image data contains an error
 * @param {Object} imageData - Image generation data object
 * @returns {Object|null} Error info if present, null otherwise
 */
export function getImageGenerationError(imageData) {
  if (!imageData) return null;

  // Check if imageData has error at top level
  if (isToolError(imageData)) {
    return {
      toolName: "image_generation",
      error: getToolErrorMessage(imageData) || "Image generation failed",
      errorType: "image_generation",
    };
  }

  // Check if error is in result property
  if (imageData.result && isToolError(imageData.result)) {
    return {
      toolName: "image_generation",
      error: getToolErrorMessage(imageData.result) || "Image generation failed",
      errorType: "image_generation",
    };
  }

  return null;
}

/**
 * Check if canvas data contains an error
 * @param {Object} canvasData - Canvas data object
 * @returns {Object|null} Error info if present, null otherwise
 */
export function getCanvasError(canvasData) {
  if (!canvasData) return null;

  if (isToolError(canvasData)) {
    return {
      toolName: "canvas",
      error: getToolErrorMessage(canvasData) || "Canvas creation failed",
      errorType: "canvas",
    };
  }

  return null;
}

/**
 * Get all tool errors from extracted tool data
 * @param {Object} toolData - Extracted tool data from message
 * @returns {Array} Array of all tool errors
 */
export function getAllToolErrors(toolData) {
  const errors = [];

  if (!toolData) return errors;

  // Check web search errors
  if (toolData.webSearchData) {
    const webError = getWebSearchError(toolData.webSearchData);
    if (webError) errors.push(webError);
  }

  // Check image generation errors
  if (toolData.imageData) {
    const imageError = getImageGenerationError(toolData.imageData);
    if (imageError) errors.push(imageError);
  }

  // Check canvas errors
  if (toolData.canvasData) {
    const canvasError = getCanvasError(toolData.canvasData);
    if (canvasError) errors.push(canvasError);
  }

  // Check tool calls for any other errors
  if (toolData.toolCalls && Array.isArray(toolData.toolCalls)) {
    const toolCallErrors = extractToolErrors(toolData.toolCalls);
    // Filter out duplicates (errors already captured by specific checks above)
    const newErrors = toolCallErrors.filter(
      (err) =>
        !errors.some(
          (existing) =>
            existing.toolName === err.toolName ||
            existing.errorType === err.errorType
        )
    );
    errors.push(...newErrors);
  }

  return errors;
}

/**
 * Check if all tool results are errors (used to skip follow-up stream)
 * @param {Array} toolResults - Array of tool result objects from streamProviderChat
 * @returns {boolean} True if all tools failed
 */
export function areAllToolsFailed(toolResults) {
  if (!Array.isArray(toolResults) || toolResults.length === 0) return false;

  return toolResults.every((result) => {
    const toolResult = result.result || result;
    return result.success === false || isToolError(toolResult);
  });
}

/**
 * Check if any tool result has an error
 * @param {Array} toolResults - Array of tool result objects from streamProviderChat
 * @returns {boolean} True if any tool failed
 */
export function hasAnyToolError(toolResults) {
  if (!Array.isArray(toolResults) || toolResults.length === 0) return false;

  return toolResults.some((result) => {
    const toolResult = result.result || result;
    return result.success === false || isToolError(toolResult);
  });
}
